import React, { useMemo } from 'react';
import { BarChart3, Users, Phone, PhoneMissed, Settings, HelpCircle, LogOut, Building2, ClipboardList, Plug2, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { PRIMARY_APP_TABS, SECONDARY_APP_TABS } from '../config/navigation';

const iconsByTabId = {
  dashboard: BarChart3,
  employees: Users,
  calls: Phone,
  missed: PhoneMissed,
  evaluation: ClipboardList,
  clients: Building2,
  complaints: AlertTriangle,
  integrations: Plug2,
  settings: Settings,
  help: HelpCircle
};

const Sidebar = ({ activeSection, onSectionChange, onLogout = () => {}, user, canAccess }) => {
  const navigationItems = useMemo(
    () => PRIMARY_APP_TABS.filter((item) => canAccess(item.id)),
    [canAccess]
  );

  const secondaryItems = useMemo(
    () => SECONDARY_APP_TABS.filter((item) => canAccess(item.id)),
    [canAccess]
  );

  return (
    <div className="flex flex-col h-screen w-64 bg-white border-r border-gray-200 dark:bg-gray-900 dark:border-gray-700 sticky top-0 self-start">
      {/* Логотип/Бренд */}
      <div className="flex items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
            <Phone className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Колл-центр</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Аналитика</p>
          </div>
        </div>
      </div>

      {/* Навигация */}
      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
        <div className="space-y-1">
          {navigationItems.map((item) => {
            const Icon = iconsByTabId[item.id];
            const isActive = activeSection === item.id;
            
            return (
              <Button
                key={item.id}
                variant={isActive ? "secondary" : "ghost"}
                className={`w-full justify-start py-2 px-3 ${
                  isActive 
                    ? 'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800' 
                    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                }`}
                onClick={() => onSectionChange(item.id)}
              >
                <Icon className={`h-5 w-5 mr-3 ${
                  isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                }`} />
                <span className="font-medium text-sm">{item.label}</span>
              </Button>
            );
          })}
        </div>

        <Separator className="my-6" />

        <div className="space-y-1">
          {secondaryItems.map((item) => {
            const Icon = iconsByTabId[item.id];
            
            return (
              <Button
                key={item.id}
                variant="ghost"
                className="w-full justify-start py-2 px-3 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                onClick={() => onSectionChange(item.id)}
              >
                <Icon className="h-4 w-4 mr-3" />
                <span className="text-sm">{item.label}</span>
              </Button>
            );
          })}
        </div>
      </nav>

      {/* Профиль пользователя */}
      <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 mt-auto">
        <div className="flex items-center space-x-3 mb-3">
          <Avatar className="h-9 w-9">
            <AvatarImage src="/api/placeholder/36/36" alt="Пользователь" />
            <AvatarFallback className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              {(user?.username ?? 'ИП').slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {user?.username ?? 'Иван Петров'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {user?.role ?? 'Менеджер'}
            </p>
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start py-2 px-2 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
          onClick={onLogout}
        >
          <LogOut className="h-4 w-4 mr-2" />
          <span className="text-sm">Выйти</span>
        </Button>
      </div>
    </div>
  );
};

export default Sidebar;
